import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { User, getIdTokenResult, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  DocumentData,
  collection,
  documentId,
  getCountFromServer,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { statsAuth, statsDb } from '../visitorStatsFirebase';
import UserCountLogin from './UserCountLogin';

type Site = { id: string; name: string };
type SiteSummary = Site & { visitors: number; pageViews: number };
type TrendPoint = { date: string; visitors: number; pageViews: number };

const SITES: Site[] = [
  { id: 'vuza', name: 'VUZA Group' },
  { id: 'allta', name: 'ALLTA' },
  { id: 'firestone', name: 'Firestone' },
  { id: 'buttered', name: 'Buttered' },
  { id: 'hongdae', name: 'Hongdae Pocha' },
  { id: 'joie', name: 'Joie' },
];

const STATS_TIME_ZONE = process.env.REACT_APP_STATS_TIME_ZONE || 'Australia/Sydney';
const ADMIN_SESSION_CACHE_KEY = 'vuza-visitor-stats-admin-session';
const ADMIN_SESSION_CACHE_MS = 30 * 24 * 60 * 60 * 1000;

const hasCachedAdminSession = (): boolean => {
  try {
    const cached = JSON.parse(localStorage.getItem(ADMIN_SESSION_CACHE_KEY) || 'null');
    if (typeof cached?.expiresAt === 'number' && cached.expiresAt > Date.now()) return true;
    localStorage.removeItem(ADMIN_SESSION_CACHE_KEY);
  } catch {
    localStorage.removeItem(ADMIN_SESSION_CACHE_KEY);
  }
  return false;
};

const cacheAdminSession = () => localStorage.setItem(
  ADMIN_SESSION_CACHE_KEY,
  JSON.stringify({ expiresAt: Date.now() + ADMIN_SESSION_CACHE_MS }),
);
const clearAdminSessionCache = () => localStorage.removeItem(ADMIN_SESSION_CACHE_KEY);

const formatDate = (date: Date): string => {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: STATS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(item => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

const currentMonth = (): string => formatDate(new Date()).slice(0, 7);
const monthSeries = (month: string): string[] => {
  const [year, monthNumber] = month.split('-').map(Number);
  const days = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return Array.from({ length: days }, (_, index) =>
    `${month}-${String(index + 1).padStart(2, '0')}`,
  );
};
const shiftMonth = (month: string, offset: number): string => {
  const [year, monthNumber] = month.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, monthNumber - 1 + offset, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (month: string): string => {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, monthNumber - 1, 1)));
};
const numeric = (value: unknown): number => typeof value === 'number' ? value : 0;

const DashboardIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const HistoryIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h8L20 8.5v10A2.5 2.5 0 0 1 17.5 21h-11A2.5 2.5 0 0 1 4 18.5z" />
    <path d="M14 3v6h6M8 13h8M8 17h6" />
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 7v5h-5M4 17v-5h5" />
    <path d="M18.2 9A7 7 0 0 0 6.1 6.5L4 9M5.8 15A7 7 0 0 0 17.9 17.5L20 15" />
  </svg>
);

const Page = styled.main`
  min-height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  background: #eaf0f6;
  color: #17202a;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 700px) { padding: 0; background: #f7f9fc; }
`;

const AppFrame = styled.div`
  width: min(1480px, 100%);
  min-height: calc(100vh - 48px);
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid #d8e0e8;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 20px 60px rgba(27, 54, 80, 0.1);

  @media (max-width: 900px) {
    min-height: 100vh;
    grid-template-columns: 1fr;
    border: 0;
    border-radius: 0;
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  padding: 36px 18px 24px;
  background: #f0f4f8;
  border-right: 1px solid #dce3ea;

  @media (max-width: 900px) {
    padding: 18px 20px 12px;
    border-right: 0;
    border-bottom: 1px solid #dce3ea;
  }
`;

const Brand = styled.div`
  padding: 0 14px;
  color: #0d4e7d;
  strong { display: block; font-size: 24px; letter-spacing: -1.2px; }
  span { display: block; margin-top: 5px; color: #657180; font-size: 12px; font-weight: 600; }

  @media (max-width: 900px) { display: none; }
`;

const SideNav = styled.nav`
  display: grid;
  gap: 8px;
  margin-top: 48px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    margin-top: 0;
  }
`;

const NavLink = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 15px 18px;
  border-radius: 11px;
  background: ${p => p.$active ? '#125681' : 'transparent'};
  color: ${p => p.$active ? '#fff' : '#46515e'};
  text-decoration: none;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover { background: ${p => p.$active ? '#125681' : '#e3eaf1'}; }
  svg {
    width: 21px; height: 21px; flex: none; fill: none;
    stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round;
  }

  @media (max-width: 420px) { justify-content: center; padding: 13px 9px; }
`;

const Account = styled.div`
  margin-top: auto;
  padding: 18px 14px 0;
  border-top: 1px solid #d8e0e8;
  color: #687482;
  font-size: 11px;

  > span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    margin-top: 10px; padding: 0; border: 0; background: transparent;
    color: #0d527f; cursor: pointer; font: inherit; font-weight: 700;
  }

  @media (max-width: 900px) { display: none; }
`;

const Workspace = styled.section`min-width: 0; background: #fff;`;

const TopBar = styled.header`
  min-height: 74px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 0 42px;
  border-bottom: 1px solid #e2e7ec;
  background: #fff;

  h1 { margin: 0; font-size: 17px; font-weight: 750; }
  @media (max-width: 700px) { min-height: 64px; padding: 0 20px; }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  border: 0;
  border-radius: 9px;
  background: #0f527f;
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  &:disabled { cursor: wait; opacity: 0.6; }
  svg {
    width: 17px; height: 17px; fill: none; stroke: currentColor;
    stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
  }
  &[data-loading='true'] svg { animation: rotate 0.8s linear infinite; }
  @keyframes rotate { to { transform: rotate(360deg); } }
`;

const Content = styled.div`
  position: relative;
  min-height: calc(100vh - 160px);
  padding: 46px 42px 60px;
  background: #fbfcfe;
  @media (max-width: 700px) { padding: 30px 18px 50px; }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 150px;
  background: rgba(251, 252, 254, 0.78);
  backdrop-filter: blur(2px);

  div {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 20px; border: 1px solid #dce5ec; border-radius: 12px;
    background: #fff; color: #31536d; box-shadow: 0 10px 35px rgba(25, 58, 84, 0.12);
    font-size: 13px; font-weight: 750;
  }
  span {
    width: 19px; height: 19px; box-sizing: border-box; border-radius: 50%;
    border: 2px solid #d4e1e9; border-top-color: #125681;
    animation: loading-spin 0.75s linear infinite;
  }
  @keyframes loading-spin { to { transform: rotate(360deg); } }
`;

const MonthHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 32px;

  h2 { margin: 0; color: #0b4c7c; font-size: clamp(30px, 4vw, 42px); letter-spacing: -1.6px; }
  p { margin: 10px 0 0; color: #5e6c7d; font-size: 14px; }
  @media (max-width: 700px) { align-items: flex-start; flex-direction: column; }
`;

const MonthPicker = styled.div`
  display: flex;
  gap: 8px;
  max-width: min(100%, 650px);
  overflow-x: auto;
  padding-bottom: 3px;
`;

const MonthLink = styled.a<{ $active?: boolean }>`
  flex: none;
  padding: 9px 13px;
  border: 1px solid ${p => p.$active ? '#145986' : '#dce3ea'};
  border-radius: 9px;
  background: ${p => p.$active ? '#e8f2f8' : '#fff'};
  color: ${p => p.$active ? '#0d527f' : '#596574'};
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
`;

const Metrics = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  @media (max-width: 1120px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const MetricCard = styled.article`
  min-height: 142px;
  padding: 24px 26px;
  box-sizing: border-box;
  border: 1px solid #dce3e9;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 10px rgba(24, 52, 78, 0.06);

  > span { color: #5c6979; font-size: 12px; font-weight: 800; letter-spacing: 0.4px; }
  strong { display: block; margin-top: 13px; color: #17202a; font-size: clamp(32px, 4vw, 44px); letter-spacing: -1.5px; }
  &:first-child strong { color: #0b4f80; }
  small { display: block; margin-top: 8px; color: #8a95a2; font-size: 11px; }
`;

const Panel = styled.section`
  margin-top: 24px;
  padding: 26px;
  border: 1px solid #dce3e9;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 10px rgba(24, 52, 78, 0.05);

  h3 { margin: 0; font-size: 17px; }
  > p { margin: 7px 0 0; color: #7c8896; font-size: 12px; }
  @media (max-width: 520px) { padding: 20px 16px; }
`;

const Chart = styled.div`
  height: 310px;
  display: flex;
  align-items: flex-end;
  gap: clamp(2px, 0.55vw, 8px);
  margin-top: 28px;
  padding: 0 4px;
  border-bottom: 1px solid #ccd6df;
  background: repeating-linear-gradient(to top, transparent 0, transparent 61px, #edf1f4 62px);
`;

const BarGroup = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  &:hover div { filter: brightness(1.15); }
`;

const Bar = styled.div<{ $height: number; $views?: boolean }>`
  flex: 1;
  min-width: 2px;
  min-height: ${p => p.$height > 0 ? '3px' : '0'};
  height: ${p => p.$height}%;
  border-radius: 3px 3px 0 0;
  background: ${p => p.$views ? '#75a9c8' : '#135681'};
  transition: height 0.25s ease;
`;

const Legend = styled.div`
  display: flex;
  gap: 18px;
  margin-top: 16px;
  color: #657281;
  font-size: 11px;
  span::before { content: ''; display: inline-block; width: 9px; height: 9px; margin-right: 6px; border-radius: 2px; background: #135681; }
  span:last-child::before { background: #75a9c8; }
`;

const TableWrap = styled.div`margin: 22px -26px -26px; overflow-x: auto; @media (max-width: 520px) { margin: 18px -16px -20px; }`;
const SiteTable = styled.table`
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
  th, td { padding: 17px 26px; border-top: 1px solid #e1e6eb; text-align: right; }
  th { background: #f2f5f9; color: #607083; font-size: 11px; letter-spacing: 0.3px; }
  td { color: #27313c; font-size: 13px; font-weight: 650; }
  th:first-child, td:first-child { text-align: left; }
  td:first-child { color: #0d527f; font-weight: 750; }
`;

const ErrorMessage = styled.p`
  margin: 0 0 20px; padding: 13px 16px; border-radius: 9px;
  background: #fff0ee; color: #b54135; font-size: 13px;
`;

const UserCount: React.FC = () => {
  const cachedAdminSession = useMemo(hasCachedAdminSession, []);
  const liveMonth = currentMonth();
  const requestedMonth = new URLSearchParams(window.location.search).get('month') || '';
  const isHistory = window.location.pathname.startsWith('/user_count/history');
  const selectedMonth = isHistory
    && /^20\d{2}-(0[1-9]|1[0-2])$/.test(requestedMonth)
    && requestedMonth < liveMonth
    ? requestedMonth
    : isHistory ? shiftMonth(liveMonth, -1) : liveMonth;
  const historyMonths = useMemo(
    () => Array.from({ length: 12 }, (_, index) => shiftMonth(liveMonth, -(index + 1))),
    [liveMonth],
  );

  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(cachedAdminSession);
  const [authResolved, setAuthResolved] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(!cachedAdminSession);
  const [loading, setLoading] = useState(cachedAdminSession);
  const [error, setError] = useState('');
  const [todayVisitors, setTodayVisitors] = useState(0);
  const [monthVisitors, setMonthVisitors] = useState(0);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [trend, setTrend] = useState<TrendPoint[]>([]);

  useEffect(() => onAuthStateChanged(statsAuth, async nextUser => {
    setUser(nextUser);
    if (!nextUser) {
      clearAdminSessionCache();
      setIsAdmin(false);
      setAuthResolved(true);
      setCheckingAuth(false);
      setLoading(false);
      return;
    }
    try {
      const token = await getIdTokenResult(nextUser, true);
      const admin = token.claims.visitorStatsAdmin === true;
      if (admin) cacheAdminSession(); else clearAdminSessionCache();
      setIsAdmin(admin);
    } finally {
      setAuthResolved(true);
      setCheckingAuth(false);
    }
  }), []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const dates = monthSeries(selectedMonth);
      const today = formatDate(new Date());
      const firstDate = dates[0];
      const lastDate = dates[dates.length - 1];
      const visitors = collection(statsDb, 'daily_visitors');
      const dailyStats = collection(statsDb, 'daily_stats');
      const [todayCount, selectedMonthCount, pageViewDocs, ...dailyCounts] = await Promise.all([
        getCountFromServer(query(visitors, where('date', '==', today))),
        getCountFromServer(query(visitors, where('date', '>=', firstDate), where('date', '<=', lastDate))),
        getDocs(query(dailyStats, where('date', '>=', firstDate), where('date', '<=', lastDate))),
        ...dates.map(date => getCountFromServer(query(visitors, where('date', '==', date)))),
      ]);

      const viewsByDate = new Map<string, number>();
      const monthViewsBySite = new Map<string, { name: string; views: number }>();
      pageViewDocs.forEach(snapshot => {
        const data = snapshot.data() as DocumentData;
        const date = String(data.date || '');
        const siteId = String(data.siteId || '');
        const views = numeric(data.pageViews);
        viewsByDate.set(date, (viewsByDate.get(date) || 0) + views);
        const previous = monthViewsBySite.get(siteId);
        monthViewsBySite.set(siteId, {
          name: String(data.siteName || previous?.name || siteId),
          views: (previous?.views || 0) + views,
        });
      });

      const summaries = await Promise.all(SITES.map(async site => {
        const count = await getCountFromServer(query(
          visitors,
          where(documentId(), '>=', `${site.id}_${firstDate}_`),
          where(documentId(), '<=', `${site.id}_${lastDate}_\uf8ff`),
        ));
        const view = monthViewsBySite.get(site.id);
        return {
          ...site,
          name: view?.name || site.name,
          visitors: count.data().count,
          pageViews: view?.views || 0,
        };
      }));

      setTodayVisitors(todayCount.data().count);
      setMonthVisitors(selectedMonthCount.data().count);
      setSites(summaries);
      setTrend(dates.map((date, index) => ({
        date,
        visitors: dailyCounts[index].data().count,
        pageViews: viewsByDate.get(date) || 0,
      })));
    } catch (caught) {
      console.error(caught);
      setError('통계를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (authResolved && isAdmin) void loadDashboard();
  }, [authResolved, isAdmin, loadDashboard]);

  const totalPageViews = useMemo(
    () => trend.reduce((sum, point) => sum + point.pageViews, 0),
    [trend],
  );
  const activeSites = useMemo(
    () => sites.filter(site => site.visitors > 0 || site.pageViews > 0).length,
    [sites],
  );
  const chartMax = useMemo(
    () => Math.max(1, ...trend.flatMap(point => [point.visitors, point.pageViews])),
    [trend],
  );

  const restoringCachedSession = cachedAdminSession && !authResolved;
  if (checkingAuth || (!restoringCachedSession && (!user || !isAdmin))) {
    return <UserCountLogin user={user} checkingAuth={checkingAuth} />;
  }

  return (
    <Page>
      <AppFrame>
        <Sidebar>
          <Brand><strong>VUZA GROUP</strong><span>Analytics View</span></Brand>
          <SideNav>
            <NavLink href="/user_count" $active={!isHistory}><DashboardIcon />대시보드</NavLink>
            <NavLink href={`/user_count/history?month=${shiftMonth(liveMonth, -1)}`} $active={isHistory}><HistoryIcon />지난 기록</NavLink>
          </SideNav>
          <Account>
            <span title={user?.email || undefined}>{user?.email}</span>
            <button onClick={() => signOut(statsAuth)}>로그아웃</button>
          </Account>
        </Sidebar>

        <Workspace>
          <TopBar>
            <h1>{isHistory ? '지난 기록' : '사용자 통계 대시보드'}</h1>
            <RefreshButton disabled={loading} onClick={loadDashboard} data-loading={loading}>
              <RefreshIcon />{loading ? '불러오는 중…' : '새로고침'}
            </RefreshButton>
          </TopBar>

          <Content>
            {loading && (
              <LoadingOverlay role="status" aria-live="polite">
                <div><span />통계 데이터를 불러오고 있습니다</div>
              </LoadingOverlay>
            )}
            <MonthHeader>
              <div><h2>{monthLabel(selectedMonth)}</h2><p>◷ {STATS_TIME_ZONE} 기준</p></div>
              {isHistory && (
                <MonthPicker>
                  {historyMonths.map(month => (
                    <MonthLink
                      key={month}
                      href={`/user_count/history?month=${month}`}
                      $active={month === selectedMonth}
                    >
                      {monthLabel(month)}
                    </MonthLink>
                  ))}
                </MonthPicker>
              )}
            </MonthHeader>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Metrics>
              <MetricCard>
                <span>{isHistory ? '월간 방문자' : '오늘 방문자'}</span>
                <strong>{(isHistory ? monthVisitors : todayVisitors).toLocaleString()}</strong>
                <small>고유 방문자 기준</small>
              </MetricCard>
              <MetricCard>
                <span>월간 방문자 합계</span>
                <strong>{monthVisitors.toLocaleString()}</strong>
                <small>일별 고유 방문자의 합</small>
              </MetricCard>
              <MetricCard>
                <span>페이지뷰</span>
                <strong>{totalPageViews.toLocaleString()}</strong>
                <small>선택한 달의 전체 조회 수</small>
              </MetricCard>
              <MetricCard>
                <span>운영 사이트</span>
                <strong>{activeSites}</strong>
                <small>전체 {SITES.length}개 사이트</small>
              </MetricCard>
            </Metrics>

            <Panel>
              <h3>일별 접속 추이</h3>
              <p>막대 위에 마우스를 올리면 날짜별 방문자와 페이지뷰를 확인할 수 있습니다.</p>
              <Chart>
                {trend.map(point => (
                  <BarGroup
                    key={point.date}
                    title={`${point.date} · 방문자 ${point.visitors} · 페이지뷰 ${point.pageViews}`}
                  >
                    <Bar $height={(point.visitors / chartMax) * 100} />
                    <Bar $height={(point.pageViews / chartMax) * 100} $views />
                  </BarGroup>
                ))}
              </Chart>
              <Legend><span>방문자</span><span>페이지뷰</span></Legend>
            </Panel>

            <Panel>
              <h3>사이트별 현황</h3>
              <p>선택한 달의 사이트별 방문자 및 페이지뷰입니다.</p>
              <TableWrap>
                <SiteTable>
                  <thead><tr><th>사이트 이름</th><th>방문자</th><th>페이지뷰</th><th>상태</th></tr></thead>
                  <tbody>
                    {sites.map(site => (
                      <tr key={site.id}>
                        <td>{site.name}</td>
                        <td>{site.visitors.toLocaleString()}</td>
                        <td>{site.pageViews.toLocaleString()}</td>
                        <td>{site.visitors || site.pageViews ? '● 수집 중' : '— 기록 없음'}</td>
                      </tr>
                    ))}
                  </tbody>
                </SiteTable>
              </TableWrap>
            </Panel>
          </Content>
        </Workspace>
      </AppFrame>
    </Page>
  );
};

export default UserCount;
